import { includes } from 'lodash-es'

import { cleanObject } from '../../helpers/utils'
import {
  FetchApiError,
  FetchNotOkError,
  FetchSseError,
} from '../helpers/errors'

import { abortableFetchSSE } from './abortable'

import type { ISseResult } from './types'
import type {
  TCustomEvent,
  IAbortableFetchSSEOptions as _IAbortableFetchSSEOptions,
} from './abortable'

export interface IProFetchSSEOptions
  extends Omit<
    _IAbortableFetchSSEOptions,
    | 'onGeneratingError'
    | 'onFetchResponse'
    | 'onEvent'
    | 'onCustomEvent'
    | 'onGenerated'
  > {
  callbacks: {
    start?: () => void
    answer?: (content: string) => void
    done?: (result: ISseResult) => void
    /** 可根据 error 判断是否服务端异常中断 */
    abort?: (result: ISseResult, error?: FetchApiError) => void
    error?: (error: FetchNotOkError | FetchApiError | Error) => void
    /** 除了上述事件外的其他事件，当前仅包含 SSE 发送的自定义事件 */
    others?: (event: TCustomEvent) => void
  }
}

export async function proFetchSSE(
  resource: string,
  options: IProFetchSSEOptions,
) {
  const { payload, callbacks = {}, ...restOptions } = options
  const { start, answer, done, abort, error, others } = callbacks
  let started = false

  try {
    await abortableFetchSSE(resource, {
      ...restOptions,
      onEvent: (event) => {
        if (event.type === 'done' && !event.serverAbort) {
          done?.(event.result)
          return
        }
        if (event.type === 'answer') {
          if (started === false) {
            start?.()
            started = true
          }
          answer?.(event.content)
        }
      },
      onGenerated(info, result) {
        if (result.type === 'Client Abort') {
          abort?.({
            content: result.content,
            words: result.words,
          })
        }
        if (result.type === 'Server Abort') {
          abort?.(
            {
              content: result.content,
              words: result.words,
            },
            new FetchApiError(result.error.code, result.error.message),
          )
        }
      },
      onGeneratingError: (code, message) => {
        error?.(new FetchSseError(code, message))
      },
      onCustomEvent(customEvent) {
        others?.(customEvent)
      },
      payload: cleanObject(payload),
      async onFetchResponse(response) {
        if (!response.ok) {
          throw new FetchNotOkError(response)
        }
        const contentType = response.headers.get('content-type')
        if (includes(contentType, 'text/event-stream')) {
          return
        }
        // 发起 SSE 请求时非流式返回异常处理
        const jsonData: { errorCode: number; errorMessage: string } =
          await response.json()
        throw new FetchApiError(jsonData.errorCode, jsonData.errorMessage)
      },
    })
  } catch (err) {
    if (err instanceof FetchNotOkError) {
      error?.(err)
    }
    if (err instanceof FetchApiError) {
      error?.(err)
    }
    if (err instanceof Error) {
      error?.(err)
    }
    // eslint-disable-next-line no-console
    console.debug(err)
  }
}
